import {
  IUiPopupSettings,
  UI_POPUP_HIDE_STRATEGY,
  UiPopupSettings,
} from '@aksia/infrastructure';
import {
  afterNextRender,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { Observable, Subject, fromEvent, takeUntil, skip } from 'rxjs';

@Directive({
  selector: '[UiPopup]',
  standalone: true,
})
export class UiPopupDirective {
  //#region Injections

  private destroyRef = inject(DestroyRef);
  public hostEl = inject(ElementRef);
  private triggerEl!: HTMLElement;

  //#endregion

  //#region Inputs/Outputs

  settings = input<UiPopupSettings, IUiPopupSettings>(new UiPopupSettings(), {
    alias: 'UiPopup',
    transform: (settings: IUiPopupSettings) => this.initSettings(settings),
  });

  @Output() UiPopupVisibilityChanged: EventEmitter<boolean> = new EventEmitter(
    false,
  );

  //#endregion

  //#region Host Bindings

  @HostBinding('attr.UiPopup') UiPopupDirective = true;

  @HostBinding('style.display')
  get uiDisplay() {
    return this.currentDisplay();
  }

  //#endregion

  //#region Properties

  hideStrategy: WritableSignal<string> = signal(this.settings().hideStrategy!);
  skipOutsideClick = signal(this.settings().skipOutsideClick ?? 0);

  currentDisplay: WritableSignal<'none' | undefined> = signal('none');
  popUpListener$?: Observable<any>;
  popUpClosed$: Subject<boolean> = new Subject<boolean>();

  #parentNode: HTMLElement | null = null;

  get isVisible(): boolean {
    return this.currentDisplay() !== 'none';
  }

  //#endregion

  constructor() {
    afterNextRender(() => {
      if (this.settings().renderInRoot) {
        this.renderInRoot();
      } else if (this.settings().renderInElement) {
        this.renderInElement();
      }
    });
  }

  //#region Functions

  protected initSettings(settings: IUiPopupSettings): IUiPopupSettings {
    this.hideStrategy.set(
      settings.hideStrategy ?? UiPopupSettings.defaultPopupHideStrategy,
    );
    this.skipOutsideClick.set(settings.skipOutsideClick ?? 0);
    return Object.assign(new UiPopupSettings(), settings);
  }

  private renderInRoot() {
    this.#parentNode = this.hostEl.nativeElement.parentNode;
    let rootEl = this.#parentNode?.closest('dialog') ?? document.body;

    let uiPopupContainer = rootEl.lastElementChild as HTMLElement;
    if (!uiPopupContainer?.hasAttribute('uipopupcontainer')) {
      uiPopupContainer = document.createElement('div');
      uiPopupContainer.setAttribute('uipopupcontainer', '');
      uiPopupContainer!.style.position = 'fixed';
      uiPopupContainer!.style.top = '0px';
      uiPopupContainer!.style.left = '0px';
      rootEl.appendChild(uiPopupContainer);
    }

    uiPopupContainer.appendChild(this.hostEl.nativeElement);
  }

  private renderInElement() {
    if (!this.settings().renderInElement?.hasAttribute('uipopupcontainer')) {
      throw new Error(
        'RenderInElement is not a valid element. Attribute uipopupcontainer is missing.',
      );
    }

    this.#parentNode = this.hostEl.nativeElement.parentNode;
    this.settings().renderInElement!.style.position = 'fixed';
    this.settings().renderInElement!.style.top = '0px';
    this.settings().renderInElement!.style.left = '0px';
    this.settings().renderInElement!.appendChild(this.hostEl.nativeElement);
  }

  public togglePopup(triggerEl?: HTMLElement) {
    this.triggerEl = triggerEl ?? this.hostEl.nativeElement;
    if (this.currentDisplay() === 'none') {
      this.currentDisplay.set(undefined);
      this.attachCloseListener();

      if (this.settings().renderInRoot || this.settings().renderInElement) {
        this.calculateUiPopupPosition();
      }

      this.UiPopupVisibilityChanged.emit(true);
    } else if (this.settings().forceHideOnToggle) {
      this.hidePopup();
    }
  }

  public hidePopup() {
    this.currentDisplay.set('none');
    this.popUpClosed$.next(true);
    this.popUpListener$ = undefined;
    this.UiPopupVisibilityChanged.emit(false);
  }

  private attachCloseListener() {
    if (!this.popUpListener$) {
      this.popUpListener$ = fromEvent(document, 'click');
      this.popUpListener$
        .pipe(takeUntil(this.popUpClosed$), skip(this.skipOutsideClick()))
        .subscribe((event: Event) => {
          const targetEl = event.target as HTMLElement;
          if (this.shouldClose(targetEl)) {
            this.currentDisplay.set('none');
            this.popUpClosed$.next(true);
            this.popUpListener$ = undefined;
            this.UiPopupVisibilityChanged.emit(false);
          }
        });
    }
  }

  private shouldClose(targetEl: HTMLElement) {
    if (targetEl?.hasAttribute('uipopupkeepopen')) return false;
    let targetElIsNotChild =
      !this.hostEl.nativeElement.contains(targetEl); /* &&
      targetEl.getAttribute('uipopupoption') !==
        this.settings()?.uid?.toString(); */
    switch (this.hideStrategy()) {
      case UI_POPUP_HIDE_STRATEGY.ClickOutside:
        return targetElIsNotChild;
      case UI_POPUP_HIDE_STRATEGY.ClickTrigger:
        return targetEl === this.triggerEl;
      case UI_POPUP_HIDE_STRATEGY.ClickOutsideOrOption:
        return targetElIsNotChild || targetEl.hasAttribute('uipopupoption');
      case UI_POPUP_HIDE_STRATEGY.ClickOutsideOrTrigger:
        return targetElIsNotChild || targetEl === this.triggerEl;
      default:
        return true;
    }
  }

  private calculateUiPopupPosition() {
    let renderElement = this.hostEl.nativeElement.parentNode;
    let coordinates = this.#parentNode?.getBoundingClientRect();
    this.hostEl.nativeElement.style.top =
      coordinates!.bottom - renderElement?.getBoundingClientRect().top + 'px';

    if (this.hostEl.nativeElement.classList.contains('right')) {
      this.hostEl.nativeElement.style.right =
        renderElement.getBoundingClientRect()?.right -
        (coordinates?.right ?? 0) +
        'px';
    } else {
      this.hostEl.nativeElement.style.left = coordinates?.left + 'px';
    }
  }

  //#endregion
}
